import { Link } from 'react-router-dom';
import { FileCheck, GitCompareArrows } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { gbp, catOf, STATUS_LABELS } from '../lib/shop';
import { toast } from 'sonner';

export default function ProductCard({ product: p }) {
  const { add, toggleCompare, inCompare } = useCart();
  const cat = catOf(p.category);
  const purchasable = !p.has_variants && p.status === 'available' && p.in_stock;
  const sub = p.has_variants
    ? `${p.variant_count} STRENGTHS · ${p.format}`
    : [p.strength, p.format].filter(Boolean).join(' · ');

  return (
    <div data-testid={`product-card-${p.slug}`} className="group bg-white border hairline hover:border-[#0B0F12] transition-colors flex flex-col">
      <Link to={`/product/${p.slug}`} className="relative block aspect-square overflow-hidden bg-[#F4F4F0]">
        <img src={p.primary_image} alt={p.name} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {p.badge && <span className="bg-[#0B0F12] text-white font-mono2 text-[9px] uppercase tracking-widest px-2 py-1">{p.badge}</span>}
          {p.new_arrival && <span className="bg-white border hairline font-mono2 text-[9px] uppercase tracking-widest px-2 py-1">New</span>}
        </div>
        {p.coa_available && (
          <span className="absolute top-2 right-2 bg-white/95 border hairline px-1.5 py-1 flex items-center gap-1" title="Certificate of Analysis available">
            <FileCheck className="w-3.5 h-3.5" />
            <span className="font-mono2 text-[9px] uppercase tracking-wider">COA</span>
          </span>
        )}
      </Link>
      <div className="p-3 sm:p-4 flex flex-col flex-1">
        <p className="font-mono2 text-[9px] sm:text-[10px] uppercase tracking-[0.15em] mb-1.5" style={{ color: cat.color }}>{cat.name}</p>
        <Link to={`/product/${p.slug}`} data-testid={`product-link-${p.slug}`}
          className="font-display font-black uppercase tracking-wide text-base sm:text-lg leading-tight hover:underline">
          {p.name}
        </Link>
        <p className="font-mono2 text-[10px] sm:text-[11px] uppercase tracking-wider text-[#8A8F95] mt-1">{sub}</p>
        <div className="mt-auto pt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-1.5">
              {p.has_variants && (p.price_from ?? 0) > 0 && <span className="font-mono2 text-[9px] uppercase tracking-widest text-[#8A8F95]">From</span>}
              <span data-testid={`product-price-${p.slug}`} className="font-mono2 font-bold text-sm sm:text-base">
                {(p.price_from ?? p.effective_price ?? p.retail_price) > 0 ? gbp(p.price_from ?? p.effective_price ?? p.retail_price) : 'TBC'}
              </span>
              {!p.has_variants && p.compare_at_price > (p.effective_price ?? p.retail_price) && (
                <span className="font-mono2 text-[10px] text-[#8A8F95] line-through">{gbp(p.compare_at_price)}</span>
              )}
            </div>
            <span className="flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-wider text-[#575B60]">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: (p.status === 'available' && p.in_stock) ? '#10B981' : '#F59E0B' }} />
              {p.status === 'available' ? (p.in_stock ? 'In stock' : 'Out of stock') : (STATUS_LABELS[p.status] || p.status)}
            </span>
          </div>
          <div className="flex gap-1.5 mt-3">
            {purchasable ? (
              <button data-testid={`add-to-basket-${p.slug}`} onClick={() => add(p)}
                className="flex-1 bg-[#0B0F12] text-[#FAFAF8] py-2.5 font-mono2 text-[10px] uppercase tracking-[0.15em] hover:bg-[#E54D2E] transition-colors">
                Add to Basket
              </button>
            ) : (
              <Link to={`/product/${p.slug}`} data-testid={`view-product-${p.slug}`}
                className="flex-1 border border-[#0B0F12] text-center py-2.5 font-mono2 text-[10px] uppercase tracking-[0.15em] hover:bg-[#F4F4F0] transition-colors">
                {p.has_variants ? 'Select Strength' : 'View Product'}
              </Link>
            )}
            <button data-testid={`compare-toggle-${p.slug}`} onClick={() => { toggleCompare(p); if (!inCompare(p.id)) toast.success('Added to comparison'); }}
              className={`w-10 border flex items-center justify-center transition-colors ${inCompare(p.id) ? 'bg-[#0B0F12] text-white border-[#0B0F12]' : 'border-[#D8D8D0] hover:border-[#0B0F12]'}`}
              title="Compare">
              <GitCompareArrows className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
